import { useEffect, useState } from 'react';
import { CaretDown, CheckCircle, DownloadSimple, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { cn } from '@/lib/utils';
import { HttpError } from '@/lib/https';
import { EXPORT_POLL_WINDOW_MS, useReportExportStatus } from '../hooks/use-analytics';
import { createReportExport, downloadReportExport } from '../services/analytics.service';
import type {
  CreateReportExportRequest,
  ReportExportFormat,
  ReportExportKind,
} from '../types/analytics-api.types';
import { analyticsErrorStatus, getAnalyticsErrorMessage } from '../utils/analytics-errors';
import { fmtBytes, fmtDateTime, fmtNum } from '../utils/report-format';

const STORAGE_PREFIX = 'campaign-reports-export:';

type StoredExport = {
  itemId: string;
  body: CreateReportExportRequest;
};

const storageKey = (report: ReportExportKind, campaignId?: string) =>
  `${STORAGE_PREFIX}${report}:${campaignId || 'org'}`;

const readStored = (key: string): StoredExport | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredExport;
    if (!parsed?.itemId) return null;
    return parsed;
  } catch {
    return null;
  }
};

const REPORT_LABEL: Record<string, string> = {
  overview: 'Dashboard overview',
  deliverability: 'Deliverability',
  'campaign-report': 'Campaign report',
  'campaign-events': 'Recipient events',
  'campaign-links': 'Link report',
};

type ReportExportControlProps = {
  report: ReportExportKind;
  /** Distinguishes a campaign export from the org-wide one in local storage. */
  scopeId?: string | null;
  getFilters: () => Omit<CreateReportExportRequest, 'report' | 'format'>;
  formats?: ReportExportFormat[];
  className?: string;
};

export function ReportExportControl({
  report,
  scopeId,
  getFilters,
  formats = ['csv'],
  className,
}: ReportExportControlProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [itemId, setItemId] = useState<string | null>(
    () => readStored(storageKey(report, scopeId || undefined))?.itemId ?? null
  );
  /** True only after Export is clicked in this view. A restored job is checked once. */
  const [follow, setFollow] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const status = useReportExportStatus(itemId, { follow });
  const job = status.data;

  useEffect(() => {
    const stored = readStored(storageKey(report, scopeId || undefined));
    setFollow(false);
    setItemId(stored?.itemId ?? null);
    setError(null);
  }, [report, scopeId]);

  useEffect(() => {
    if (!itemId || !status.isError) return;
    if (analyticsErrorStatus(status.error) !== 404) return;
    localStorage.removeItem(storageKey(report, scopeId || undefined));
    setFollow(false);
    setItemId(null);
  }, [itemId, report, scopeId, status.error, status.isError]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!(e.currentTarget instanceof Document)) return;
      const root = document.getElementById(`export-${report}`);
      if (root && !root.contains(target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [menuOpen, report]);

  const persist = (next: StoredExport, campaignId?: string) => {
    localStorage.setItem(storageKey(report, campaignId), JSON.stringify(next));
    setItemId(next.itemId);
  };

  const start = async (format: ReportExportFormat) => {
    const filters = getFilters();
    const body: CreateReportExportRequest = { report, format, ...filters };
    const idempotencyKey = crypto.randomUUID();
    setMenuOpen(false);
    setError(null);
    setStarting(true);
    try {
      const created = await createReportExport(body, idempotencyKey);
      setFollow(true);
      persist({ itemId: created.itemId, body }, scopeId || filters.campaignId);
    } catch (caught) {
      setError(getAnalyticsErrorMessage(caught));
    } finally {
      setStarting(false);
    }
  };

  const download = async () => {
    if (!itemId) return;
    setError(null);
    try {
      const file = await downloadReportExport(itemId);
      const url = URL.createObjectURL(file.blob);
      const anchor = Object.assign(document.createElement('a'), {
        href: url,
        download: file.fileName,
      });
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (caught) {
      if (caught instanceof HttpError && (caught.status === 410 || caught.status === 409)) {
        setError(getAnalyticsErrorMessage(caught));
        return;
      }
      setError(getAnalyticsErrorMessage(caught));
    }
  };

  const label = REPORT_LABEL[report] || 'Report';
  const inProgress = job?.status === 'queued' || job?.status === 'in_progress';
  const requestedAt = job?.requestedAt ? Date.parse(job.requestedAt) : NaN;
  const withinWindow =
    !Number.isFinite(requestedAt) || Date.now() - requestedAt < EXPORT_POLL_WINDOW_MS;
  const following = follow && inProgress && withinWindow;
  const pending = starting || (!!itemId && status.isLoading) || following;

  if (pending) {
    return (
      <div className={cn('flex flex-col items-end self-start', className)}>
        <div className="inline-flex items-center gap-2.5 rounded-md border border-neutral-300 bg-white px-3.5 py-2">
          <span
            className="inline-block size-3.5 animate-spin rounded-full border-2 border-neutral-300 border-t-primary-600"
            aria-hidden
          />
          <span className="text-xs text-neutral-700">Preparing {label.toLowerCase()}…</span>
        </div>
        <p className="mt-1.5 max-w-[280px] text-right text-xs text-neutral-500">
          Large exports are generated in the background — you can keep working.
        </p>
      </div>
    );
  }

  if (job && inProgress) {
    return (
      <div className={cn('flex flex-col items-end self-start', className)}>
        <div className="inline-flex max-w-[360px] flex-wrap items-center gap-2 rounded-md border border-[#F0C98A] bg-[#FFFCF5] px-3.5 py-2">
          <Warning className="size-4 shrink-0 text-[#b07d10]" weight="fill" aria-hidden />
          <span className="text-xs text-neutral-800">
            This export is still waiting. Check again, or start a new one.
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => status.refetch()}
          >
            Check again
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => start((job.format === 'pdf' ? 'pdf' : 'csv') as ReportExportFormat)}
          >
            Run again
          </Button>
        </div>
      </div>
    );
  }

  if (job?.status === 'completed') {
    return (
      <div className={cn('flex flex-col items-end self-start', className)}>
        <div className="inline-flex max-w-full flex-wrap items-center gap-2.5 rounded-md border border-[#BFE3C6] bg-[#F4FBF5] px-3.5 py-2">
          <CheckCircle className="size-4 shrink-0 text-[#0ca30c]" weight="fill" aria-hidden />
          <span className="text-xs font-medium text-neutral-800">
            {label} {job.format.toUpperCase()} ready
          </span>
          <span className="text-xs text-neutral-600">
            {job.rowCount == null ? '' : `${fmtNum(job.rowCount)} rows · `}
            {fmtBytes(job.sizeBytes)}
          </span>
          <Button type="button" size="sm" className="h-8 gap-1.5 px-3 text-xs" onClick={download}>
            <DownloadSimple className="size-3.5" aria-hidden />
            Download
          </Button>
          <button
            type="button"
            className="text-xs text-neutral-600 underline hover:text-neutral-900"
            onClick={() => start((job.format === 'pdf' ? 'pdf' : 'csv') as ReportExportFormat)}
          >
            New export
          </button>
        </div>
        <p className="mt-1.5 max-w-[320px] text-right text-xs text-neutral-500">
          {job.expiresAt ? `Available until ${fmtDateTime(job.expiresAt)}. ` : ''}
          Exports and downloads are recorded in the audit log.
        </p>
        {error ? <p className="mt-1 text-right text-xs text-[#B3261E]">{error}</p> : null}
      </div>
    );
  }

  if (job?.status === 'failed' || job?.status === 'expired') {
    return (
      <div className={cn('flex flex-col items-end self-start', className)}>
        <div className="inline-flex max-w-[360px] flex-wrap items-center gap-2 rounded-md border border-[#F0C98A] bg-[#FFFCF5] px-3.5 py-2">
          <Warning className="size-4 shrink-0 text-[#b07d10]" weight="fill" aria-hidden />
          <span className="text-xs text-neutral-800">
            {job.status === 'expired'
              ? 'This export has expired. Request a new one.'
              : job.error || 'The export failed.'}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => start((job.format === 'pdf' ? 'pdf' : 'csv') as ReportExportFormat)}
          >
            Run again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div id={`export-${report}`} className={cn('relative inline-block self-start', className)}>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 gap-2 border-neutral-300 bg-white px-3.5 text-xs font-medium text-neutral-900 hover:bg-neutral-100"
        onClick={() => setMenuOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <DownloadSimple className="size-[15px]" aria-hidden />
        Export
        <CaretDown className="size-3 text-neutral-500" aria-hidden />
      </Button>
      {menuOpen && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-1 min-w-[160px] rounded-md border border-neutral-200 bg-white py-1 shadow-md"
        >
          {formats.map((format) => (
            <button
              key={format}
              type="button"
              role="menuitem"
              className="flex w-full px-3 py-2 text-left text-sm text-neutral-900 hover:bg-neutral-50"
              onClick={() => start(format)}
            >
              Export as {format.toUpperCase()}
            </button>
          ))}
        </div>
      )}
      {error ? (
        <p className="mt-1 max-w-[240px] text-right text-xs text-[#B3261E]">{error}</p>
      ) : null}
    </div>
  );
}
